import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  NavigationConfiguration,
  NavigationLink,
  NavigationStateData,
  ToolbarDropdownOption,
} from '@zipari/shared-ds-util-navigation';
import { ExternalSiteModalConfig } from '../external-site-warning-modal/external-site-warning-modal.model';
import { NavIconsService } from './components/nav-icons/nav-icons.service';
import { NavigationService } from './navigation.service';
import { checkInputsForText } from '../../design-system.helper';
import { Router } from '@angular/router';

@Component({
  selector: 'navigation',
  styleUrls: ['./navigation.component.scss'],
  templateUrl: './navigation.component.html',
})
export class NavigationComponent implements OnInit {
  @Input() config: NavigationConfiguration;
  @Input() data: NavigationStateData;
  @Input() externalSiteModalConfig: ExternalSiteModalConfig;
  @Input() headerClass: { [key: string]: boolean };
  @Input() activeLangKey: string;
  @Output() dropdownOptionSelected = new EventEmitter<object>();
  @Output() loginClicked = new EventEmitter<void>();
  @Output() logoutClicked = new EventEmitter<object>();
  @Output() mobileMenuToggled = new EventEmitter<boolean>();
  @Output() navItemClicked = new EventEmitter<NavigationLink>();
  @Output() navMenuToggled = new EventEmitter<NavigationLink>();
  @Output() languageSwitched = new EventEmitter<string>();

  homeUrl: NavigationLink;
  isContactUsModalOpen: boolean;

  constructor(
    private navigationService: NavigationService,
    public navIconsService: NavIconsService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    const [config, data, externalSiteModalConfig] = checkInputsForText([
      this.config,
      this.data,
      this.externalSiteModalConfig,
    ]);

    this.config = config as NavigationConfiguration;
    this.data = data as NavigationStateData;
    this.externalSiteModalConfig =
      externalSiteModalConfig as ExternalSiteModalConfig;

    const logoLink: NavigationLink = {
      key: 'home',
      route: this.config?.logoUrl ? this.config.logoUrl : '/',
      isExternalRoute: this.config?.isLogoUrlExternal,
    };

    this.homeUrl = this.navigationService.getLinkConfig(logoLink);
    this.initNavigationServicePropagators();
  }

  /** We can use the navigation service to propagate emits from deep components instead of chaining event emitters through many layers. */
  initNavigationServicePropagators(): void {
    this.navigationService.dropdownOptionSelected$.subscribe(
      (toolbarDropdownOption: ToolbarDropdownOption) => {
        this.dropdownOptionSelected.emit(toolbarDropdownOption);
      },
    );
    this.navigationService.loginClicked$.subscribe(() => {
      this.loginClicked.emit();
    });
    this.navigationService.logoutClicked$.subscribe((obj: object) => {
      this.logoutClicked.emit(obj);
    });
    this.navigationService.mobileMenuToggled$.subscribe((bool: boolean) => {
      this.mobileMenuToggled.emit(bool);
    });
    this.navigationService.navItemClicked$.subscribe(
      (navigationLink: NavigationLink) => {
        this.navItemClicked.emit(navigationLink);
      },
    );
    this.navigationService.navMenuToggled$.subscribe(
      (navigationLink: NavigationLink) => {
        this.navMenuToggled.emit(navigationLink);
      },
    );
  }
  public skipToMain(event) {
    event.preventDefault();
    const element: HTMLElement = document.querySelector('#main-site-content');

    element.setAttribute('tabindex', '-1'); // You can set tabindex in HTML too than in JS
    element.focus();
  }

  toggleContactUs() {
    this.isContactUsModalOpen = !this.isContactUsModalOpen;
    this.toggleMobileMenu(false);
  }

  disableClickFocus(evt: MouseEvent) {
    evt.preventDefault(); // prevent blur on clickable items to block :focus state
  }

  handleLinkClicked(link: NavigationLink) {
    this.navItemClicked.emit(link);

    if (link.items) {
      this.navMenuToggled.emit(link);
    } else if (!link.items && this.data.isMobileMenuOpen) {
      this.toggleMobileMenu(false);
    } else if (this.data.openMenuItem) {
      this.navMenuToggled.emit(null);
    }
  }

  selectOption(option: object) {
    this.dropdownOptionSelected.emit(option);
  }

  toggleMobileMenu(isOpen: boolean) {
    this.mobileMenuToggled.emit(isOpen);
  }

  logout(logoutItem?) {
    this.logoutClicked.emit(logoutItem);
  }

  login() {
    this.loginClicked.emit();
  }

  onNavMaskClick() {
    this.navMenuToggled.emit(null);
  }

  onNavMaskKeydownSpace() {
    this.navMenuToggled.emit(null);
  }

  handleLanguageSwitched(langKey: string) {
    this.languageSwitched.emit(langKey);
  }
}
